"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, ChevronDown, ChevronUp } from "lucide-react";

const MY_HAMMER_URL = "https://www.my-hammer.de/auftragnehmer/ju-garden";
const GOOGLE_MAPS_URL = "https://maps.app.goo.gl/uLc2cmCEfnYwvgv79";

const testimonials = [
  {
    text: "Von erster Besprechung bis endgültigen Durchführung des Schnittes, alles bestens abgelaufen. Wir sind sehr zufrieden und können Daniel Judin und sein Team wärmstens weiter empfehlen.",
    name: "Reinhold D.",
    city: "Detmold",
    rating: 5,
    source: "Google",
    sourceUrl: GOOGLE_MAPS_URL,
    logo: null,
  },
  {
    text: "Wir sind sehr zufrieden mit der Arbeit! Der Garten sieht jetzt wieder top aus, und alles wurde schnell und professionell erledigt. Sehr freundlicher und zuverlässiger Service – absolut empfehlenswert!",
    name: "Harun",
    city: "Lage",
    rating: 5,
    source: "my-hammer.de",
    sourceUrl: MY_HAMMER_URL,
    logo: "/img/logo/my_hammer_logo.png",
  },
  {
    text: "Super freundlicher Kundenkontakt und schnelle saubere Arbeit! Sind sehr zufrieden und freuen uns über weitere Zusammenarbeit.",
    name: "Birgitt R.",
    city: null,
    rating: 5,
    source: "Google",
    sourceUrl: GOOGLE_MAPS_URL,
    logo: null,
  },
  {
    text: "Tolle Arbeit, netter Gärtner der seine Arbeit liebt. Wärmstens weiter zu empfehlen!",
    name: "Katja C.",
    city: null,
    rating: 5,
    source: "Google",
    sourceUrl: GOOGLE_MAPS_URL,
    logo: null,
  },
  {
    text: "Gute Arbeit und top aufräumen des Arbeitsumfeldes nach der Tätigkeit. Sehr zu empfehlen!",
    name: null,
    city: "Horn-Bad Meinberg",
    rating: 5,
    source: "my-hammer.de",
    sourceUrl: MY_HAMMER_URL,
    logo: "/img/logo/my_hammer_logo.png",
  },
  {
    text: "Verlässlich pünktlich, zügig und sauber. Immer wieder gerne.",
    name: "Markus B.",
    city: null,
    rating: 5,
    source: "Google",
    sourceUrl: GOOGLE_MAPS_URL,
    logo: null,
  },
  {
    text: "Alle vereinbarten Ziele eingehalten: Termin, Qualität der Arbeit, Sauberkeit. Würde ich wieder beauftragen und jederzeit empfehlen.",
    name: null,
    city: "Blomberg",
    rating: 4,
    source: "my-hammer.de",
    sourceUrl: MY_HAMMER_URL,
    logo: "/img/logo/my_hammer_logo.png",
  },
  {
    text: "Kontakte prompt, sachorientiert und freundlich.",
    name: "Markus",
    city: "Detmold",
    rating: 5,
    source: "my-hammer.de",
    sourceUrl: MY_HAMMER_URL,
    logo: "/img/logo/my_hammer_logo.png",
  },
];

const INITIAL_COUNT = 4;

export function Testimonials() {
  const [showAll, setShowAll] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 767px)").matches,
  );
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const filtered = testimonials.filter((t) => t.text);
  const visible = showAll ? filtered : filtered.slice(0, INITIAL_COUNT);

  const reviewCount = filtered.length;
  const averageRating =
    filtered.reduce((sum, t) => sum + t.rating, 0) / reviewCount;
  const averageLabel = averageRating.toFixed(1).replace(".", ",");

  // Mobile-Erkennung
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");

    const handler = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      if (!e.matches) setActiveIndex(null);
    };

    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  // Intersection Observer nur auf Mobile
  useEffect(() => {
    if (!isMobile) return;

    const visibleIndices = new Set<number>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Number(entry.target.getAttribute("data-index"));
          if (isNaN(idx)) return;

          if (entry.isIntersecting) {
            visibleIndices.add(idx);
          } else {
            visibleIndices.delete(idx);
          }
        });

        if (visibleIndices.size > 0) {
          // Immer die Karte mit dem kleinsten Index (am weitesten oben) aktivieren
          const closest = Math.min(...visibleIndices);
          setActiveIndex(closest);
        } else {
          setActiveIndex(null);
        }
      },
      { rootMargin: "-35% 0px -35% 0px", threshold: 0 },
    );

    cardRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [isMobile, visible]);

  return (
    <section className="py-24 bg-green-950 text-primary-foreground relative overflow-hidden">
      {/* Dekoratives Hintergrund-Element für mehr Tiefe */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.1),transparent)] pointer-events-none" />

      <div className="container px-4 md:px-6 mx-auto relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl mb-4 font-serif">
            Das sagen unsere Kunden
          </h2>
          <div className="inline-flex flex-col items-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-6 py-3 backdrop-blur-sm sm:flex-row sm:gap-3">
            <span className="flex items-center gap-1" aria-hidden="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className="h-5 w-5 fill-yellow-400 text-yellow-400"
                />
              ))}
            </span>
            <span className="text-lg font-semibold text-white">
              {averageLabel} / 5
            </span>
            <span className="text-sm text-primary-foreground/80">
              Verifizierte Bewertungen auf{" "}
              <a
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-white underline underline-offset-2 hover:text-yellow-300"
              >
                Google
              </a>{" "}
              &{" "}
              <a
                href={MY_HAMMER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-white underline underline-offset-2 hover:text-yellow-300"
              >
                MyHammer
              </a>
            </span>
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
          {visible.map((testimonial, index) => {
            const isActive = activeIndex === index;
            return (
              <Card
                ref={(el) => {
                  cardRefs.current[index] = el;
                }}
                key={index}
                data-index={index}
                onClick={() => {
                  if (isMobile) {
                    setActiveIndex(isActive ? null : index);
                  }
                }}
                className={`testimonial-card bg-white/5 text-primary-foreground border border-white/10 backdrop-blur-sm shadow-sm transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-xl hover:bg-white/10 hover:border-white/20 relative overflow-hidden group ${
                  isActive
                    ? "-translate-y-1 shadow-xl bg-white/10 border-white/20"
                    : ""
                }`}
              >
                {/* Animierter feiner Farb-Akzent oben */}
                <div
                  className={`absolute top-0 left-0 w-full h-0.75 bg-linear-to-r from-white/30 to-white origin-left transition-transform duration-300 ${
                    isActive
                      ? "scale-x-100"
                      : "scale-x-0 group-hover:scale-x-100 group-active:scale-x-100"
                  }`}
                />
                <CardContent className="pt-6 h-full flex flex-col justify-between relative z-10">
                  <div>
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${
                            i < testimonial.rating
                              ? "fill-yellow-400 text-yellow-400"
                              : "fill-white/20 text-white/20"
                          }`}
                        />
                      ))}
                    </div>
                    <div className="relative mb-4">
                      <span className="text-6xl text-white/10 absolute -top-4 -left-3 font-serif select-none pointer-events-none">
                        &quot;
                      </span>
                      <p className="text-lg italic leading-relaxed text-primary-foreground/90 relative z-10 select-none">
                        {testimonial.text}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-primary-foreground/70 font-semibold">
                      — {testimonial.name ?? "Kunde"}
                      {testimonial.city ? ` aus ${testimonial.city}` : ""} · via{" "}
                      <a
                        href={testimonial.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-white transition-colors"
                      >
                        {testimonial.source}
                      </a>
                    </div>
                    {testimonial.logo ? (
                      <div className="bg-white rounded-full overflow-hidden shadow-sm">
                        <Image
                          src={testimonial.logo}
                          alt={`${testimonial.source} Logo`}
                          width={80}
                          height={32}
                          className="h-8 w-auto object-cover"
                        />
                      </div>
                    ) : (
                      <a
                        href={testimonial.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-sm font-semibold text-primary-foreground/70 hover:text-white transition-colors"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className="w-5 h-5"
                          aria-hidden
                        >
                          <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                            fill="#4285F4"
                          />
                          <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                          />
                          <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            fill="#FBBC05"
                          />
                          <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                          />
                        </svg>
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filtered.length > INITIAL_COUNT && (
          <div className="flex justify-center mt-12">
            <Button
              variant="outline"
              className="cursor-pointer bg-white/10 text-white border-white/20 hover:bg-white/20 hover:text-white"
              onClick={() => setShowAll((prev) => !prev)}
            >
              {showAll ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-2" />
                  Weniger anzeigen
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-2" />
                  Mehr anzeigen ({filtered.length - INITIAL_COUNT})
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
